/**
 * Admin-creates-user dialog.
 *
 * On submit, the backend creates the user with status=active, issues
 * a set-password token, and emails the new user an invitation with
 * the link.  The response also returns the ``set_password_url`` so
 * the admin has a fallback to hand-deliver via Slack / DM in case
 * the email never arrives (SendGrid delivery is best-effort on this
 * path so a failed send doesn't block account creation).
 *
 * Visual conventions match the rest of the admin section:
 *  - Warm ivory paper, gold-gradient accent strip at the top
 *  - Brand-tinted form fields (warm cream → white on focus)
 *  - Role pickers as gold-outlined toggle chips (no raw checkboxes)
 *  - Gold-gradient primary CTA + secondary cancel link
 */

import AutorenewIcon from '@mui/icons-material/Autorenew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { createUser, type AdminCreateUserResponse, type RoleWithUsage } from '../../api/admin';
import { updateAdminRep } from '../../api/field';
import { HomeStatePicker, TerritoryStatePicker } from '../field/StatePickers';
import { RoleCheckboxList } from './RoleCheckboxList';

const INK = '#0f0a07';
const IVORY_LIGHT = '#fdf9f0';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  roles: RoleWithUsage[];
}

export function CreateUserDialog({ open, onClose, roles }: CreateUserDialogProps) {
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [roleIds, setRoleIds] = useState<Set<string>>(new Set());
  const [success, setSuccess] = useState<AdminCreateUserResponse | null>(null);
  const [copied, setCopied] = useState(false);

  // Field-rep panel state — only sent if the field_rep role is selected.
  const [repHomeAddress, setRepHomeAddress] = useState('');
  const [repHomeCity, setRepHomeCity] = useState('');
  const [repHomeState, setRepHomeState] = useState('');
  const [repHomeZip, setRepHomeZip] = useState('');
  const [repPhone, setRepPhone] = useState('');
  const [repTerritories, setRepTerritories] = useState<string[]>([]);

  const fieldRepRoleId = roles.find((r) => r.name === 'field_rep')?.id;
  const isFieldRepSelected = !!fieldRepRoleId && roleIds.has(fieldRepRoleId);

  // Auto-default: when the admin sets the rep's home state, include it
  // in the territory list if it's not already there.  Admin can still
  // remove it via the chip's × if the rep covers neighbours only.
  useEffect(() => {
    if (!repHomeState) return;
    if (repTerritories.includes(repHomeState)) return;
    setRepTerritories([...repTerritories, repHomeState].sort((a, b) => a.localeCompare(b)));
  }, [repHomeState, repTerritories]);

  const mutation = useMutation({
    mutationFn: async () => {
      const created = await createUser({
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        role_ids: Array.from(roleIds),
      });
      // If this user is being onboarded as a field rep, save their
      // rep profile in a second call.  The backend lazy-creates the
      // row, so the PATCH lands cleanly.
      if (isFieldRepSelected) {
        await updateAdminRep(created.user.id, {
          home_address: repHomeAddress.trim() || null,
          home_city: repHomeCity.trim() || null,
          home_state: repHomeState.trim().toUpperCase() || null,
          home_zip: repHomeZip.trim() || null,
          phone: repPhone.trim() || null,
          territory_states: repTerritories,
        });
      }
      return created;
    },
    onSuccess: (data) => {
      setSuccess(data);
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'field'] });
    },
  });

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setRoleIds(new Set());
    setSuccess(null);
    setCopied(false);
    setRepHomeAddress('');
    setRepHomeCity('');
    setRepHomeState('');
    setRepHomeZip('');
    setRepPhone('');
    setRepTerritories([]);
    mutation.reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const copyLink = async () => {
    if (!success?.set_password_url) return;
    await navigator.clipboard.writeText(success.set_password_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const valid =
    firstName.trim().length > 0 && lastName.trim().length > 0 && email.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            position: 'relative',
            bgcolor: IVORY_LIGHT,
            backgroundImage: 'none',
            border: '1px solid rgba(187, 140, 63, 0.22)',
            borderRadius: 2,
            m: { xs: 2, sm: 4 },
            // Parent clips the pseudo-strip via overflow:hidden so the
            // gold accent never pokes past the rounded corners.
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, transparent 100%)`,
              zIndex: 1,
            },
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          fontSize: 17,
          color: INK,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          pt: 2.5,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'rgba(187, 140, 63, 0.12)',
            color: GOLD,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PersonAddAlt1Icon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box>{success ? 'User Added' : 'Add User'}</Box>
          {!success && (
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11.5,
                fontWeight: 500,
                color: 'rgba(15, 10, 7, 0.55)',
                mt: 0.25,
              }}
            >
              Invite a new teammate to the platform.
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ borderColor: 'rgba(187, 140, 63, 0.22)', bgcolor: IVORY_LIGHT }}
      >
        {success ? (
          <Stack spacing={2}>
            <Alert severity="success" sx={{ fontFamily: FONT_INTER, borderRadius: 1.25 }}>
              Account created and invitation email sent to <strong>{success.user.email}</strong>.
            </Alert>
            <Typography
              sx={{ fontFamily: FONT_INTER, fontSize: 13, color: 'rgba(15, 10, 7, 0.7)' }}
            >
              The email contains a one-time link to set their password. It expires in 24 hours.
            </Typography>
            {success.set_password_url && (
              <Box
                sx={{
                  borderTop: '1px solid rgba(187, 140, 63, 0.22)',
                  pt: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 11.5,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'rgba(15, 10, 7, 0.55)',
                    mb: 0.75,
                  }}
                >
                  Fallback link
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 12.5,
                    color: 'rgba(15, 10, 7, 0.6)',
                    mb: 1.25,
                  }}
                >
                  If the email doesn't arrive, copy this link and share it directly.
                </Typography>
                <TextField
                  label="Set-password link"
                  value={success.set_password_url}
                  fullWidth
                  size="small"
                  multiline
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: 11.5 },
                  }}
                  sx={inputSx}
                />
                <Button
                  startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />}
                  size="small"
                  onClick={copyLink}
                  sx={{
                    mt: 1.25,
                    fontFamily: FONT_INTER,
                    fontWeight: 600,
                    fontSize: 12,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: GOLD,
                    '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.10)' },
                  }}
                >
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
              </Box>
            )}
          </Stack>
        ) : (
          <Stack spacing={2.25}>
            {mutation.error && (
              <Alert severity="error" sx={{ fontFamily: FONT_INTER, borderRadius: 1.25 }}>
                {(mutation.error as Error).message}
              </Alert>
            )}

            <InvitationHowItWorks />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
                required
                autoFocus
                inputProps={{ maxLength: 100 }}
                sx={inputSx}
              />
              <TextField
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
                required
                inputProps={{ maxLength: 100 }}
                sx={inputSx}
              />
            </Stack>

            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              sx={inputSx}
            />

            {/* Role selector — explicit checkboxes. Selecting
                Administrator hides the other roles since admin is a
                wildcard that already grants access to every section. */}
            <Box>
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: GOLD,
                  mb: 1,
                }}
              >
                Roles
              </Typography>
              <RoleCheckboxList
                roles={roles}
                selected={roleIds}
                onChange={(next) => setRoleIds(next)}
              />
            </Box>

            {isFieldRepSelected && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(142, 77, 171, 0.06)',
                  border: '1px solid rgba(142, 77, 171, 0.22)',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: '#8e4dab',
                    mb: 0.5,
                  }}
                >
                  Representative Details
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 12.5,
                    color: 'rgba(15, 10, 7, 0.65)',
                    mb: 2,
                  }}
                >
                  Set the rep’s territory and home address now, or skip and edit later from Admin →
                  Field.
                </Typography>

                <TextField
                  label="Home address"
                  value={repHomeAddress}
                  onChange={(e) => setRepHomeAddress(e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 200 }}
                  sx={{ mb: 1.5, ...inputSx }}
                />
                <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                  <TextField
                    label="City"
                    value={repHomeCity}
                    onChange={(e) => setRepHomeCity(e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 100 }}
                    sx={inputSx}
                  />
                  <HomeStatePicker value={repHomeState} onChange={setRepHomeState} />
                  <TextField
                    label="ZIP"
                    value={repHomeZip}
                    onChange={(e) => setRepHomeZip(e.target.value)}
                    sx={{ width: 140, ...inputSx }}
                    size="small"
                    inputProps={{ maxLength: 10 }}
                  />
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <TerritoryStatePicker value={repTerritories} onChange={setRepTerritories} />
                  <Typography
                    sx={{
                      mt: 0.75,
                      fontFamily: FONT_INTER,
                      fontSize: 11.5,
                      color: 'rgba(15, 10, 7, 0.55)',
                    }}
                  >
                    Their home state is auto-included; add others if they cover more than one.
                  </Typography>
                </Box>
                <TextField
                  label="Phone"
                  value={repPhone}
                  onChange={(e) => setRepPhone(e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 30 }}
                  sx={inputSx}
                />
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {success ? (
          <Button onClick={handleClose} sx={goldButtonSx}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} sx={cancelButtonSx}>
              Cancel
            </Button>
            <Button
              disabled={mutation.isPending || !valid}
              onClick={() => mutation.mutate()}
              sx={goldButtonSx}
            >
              {mutation.isPending ? 'Creating…' : 'Create user'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * Structured "how the invitation flow works" callout shown at the top
 * of the Add user form. Replaces a prose paragraph with three labeled
 * rows so the key facts (one-time link, 24h expiry, resend if needed)
 * are immediately scannable.
 */
function InvitationHowItWorks() {
  const items: { Icon: typeof LinkIcon; title: string; detail: string }[] = [
    {
      Icon: LinkIcon,
      title: 'One-time set-password link',
      detail: 'They click it to choose their own password before signing in.',
    },
    {
      Icon: TimerOutlinedIcon,
      title: 'Valid for 24 hours',
      detail: 'After that the link expires and can no longer be used.',
    },
    {
      Icon: AutorenewIcon,
      title: 'Resend any time',
      detail: 'From the Users list — open the ⋯ menu next to their row.',
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: 'rgba(187, 140, 63, 0.06)',
        border: '1px solid rgba(187, 140, 63, 0.22)',
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: GOLD,
          mb: 1,
        }}
      >
        How the invitation works
      </Typography>
      <Stack spacing={1}>
        {items.map((it) => (
          <Box key={it.title} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                bgcolor: 'rgba(187, 140, 63, 0.14)',
                color: GOLD,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: 0.1,
              }}
            >
              <it.Icon sx={{ fontSize: 15 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: INK,
                  lineHeight: 1.3,
                }}
              >
                {it.title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 11.5,
                  color: 'rgba(15, 10, 7, 0.6)',
                  lineHeight: 1.4,
                }}
              >
                {it.detail}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255, 250, 235, 0.55)',
    fontFamily: FONT_INTER,
    fontSize: 14,
    transition: 'background-color 160ms ease',
    '& fieldset': { borderColor: 'rgba(15, 10, 7, 0.14)' },
    '&:hover': { bgcolor: 'rgba(255, 250, 235, 0.85)' },
    '&:hover fieldset': { borderColor: 'rgba(187, 140, 63, 0.4)' },
    '&.Mui-focused': { bgcolor: '#fffdf6' },
    '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontFamily: FONT_INTER, fontSize: 13.5 },
  '& .MuiInputLabel-root.Mui-focused': { color: GOLD },
} as const;

const goldButtonSx = {
  background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
  color: INK,
  fontFamily: FONT_INTER,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  fontSize: 12,
  px: 2.5,
  py: 0.85,
  boxShadow: 'none',
  borderRadius: 1,
  '&:hover': {
    background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
    boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
  },
  '&.Mui-disabled': {
    background: 'rgba(15, 10, 7, 0.08)',
    color: 'rgba(15, 10, 7, 0.3)',
  },
} as const;

const cancelButtonSx = {
  fontFamily: FONT_INTER,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  fontSize: 12,
  px: 2,
  color: 'rgba(15, 10, 7, 0.6)',
  '&:hover': { bgcolor: 'rgba(15, 10, 7, 0.04)' },
} as const;
